/**
 * Morning standup — THE INVERSION MANDATE's first unprompted word.
 *
 * The first session each day, the copilot dock opens ON ITS OWN with the
 * overnight work already done: "Morning <name>. Ready for you: N drafted
 * emails, K prepped posts, M agent proposals." This module is the PURE
 * logic behind that moment — no store, no network, no clock reads. The
 * wiring (real counts from real records, the brainFacts persistence stamp)
 * lives in standupBoot.ts; the dock transition is the tested copilotDock
 * reducer's 'auto-standup' event.
 *
 * Honesty contract:
 *   • every count the standup speaks comes from a REAL record the caller
 *     gathered (draft-first composers, prepped drafts, pending approvals) —
 *     composeStandup only formats, it never invents;
 *   • genuinely nothing ready → null. Silence is honest; a fake "all set!"
 *     greeting is not.
 *   • it never traps: the caller renders one-click chips (Review /
 *     Approve all reversible / Not now) and stamps `lastStandupShownAt`
 *     on ANY of them — once per calendar day, maximum.
 *   • no fake mornings: the show-gate (and compose itself, defense in
 *     depth) only fires between 5am and 2pm LOCAL time.
 */

/** Local-time window in which a "Morning" greeting is honest: [5am, 2pm). */
export const STANDUP_HOUR_START = 5;
export const STANDUP_HOUR_END = 14;

export interface StandupInputs {
  /** Drafted emails ready to approve (draft-first composers, real records). */
  drafts: number;
  /** Argo-prepped content drafts (preppedDrafts generator, real Brain). */
  preppedPosts: number;
  /** Pending agent proposals in the approvals inbox. */
  pendingApprovals: number;
  /**
   * The subset of pending proposals that are reversible ('auto' risk) —
   * drives the "Approve all reversible (N)" chip. Defaults to 0 (the chip
   * simply doesn't render), never to `pendingApprovals`.
   */
  reversibleApprovals?: number;
  /** Display name to greet; omitted → a plain "Morning." */
  userName?: string;
}

export interface StandupCounts {
  drafts: number;
  preppedPosts: number;
  pendingApprovals: number;
  /** Reversible pending proposals — what "Approve all reversible" acts on. */
  reversible: number;
  total: number;
}

export interface Standup {
  /** "Morning Dana." — first name only, or "Morning." when unknown. */
  greeting: string;
  /** One human line per non-zero count, e.g. "2 drafted emails". */
  lines: string[];
  counts: StandupCounts;
  /**
   * True when the workspace had nothing to brief: the standup still speaks
   * first, but as an ask for the business (run setup / add your book)
   * instead of a work summary.
   */
  coldStart?: boolean;
}

/** True while a "Morning" greeting is honest (5am–2pm local). */
export function isStandupHour(now: number | Date): boolean {
  const h = new Date(now).getHours();
  return h >= STANDUP_HOUR_START && h < STANDUP_HOUR_END;
}

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Once per calendar day, mornings only.
 *
 *   • first-ever open (no stamp) → true, provided it's within 5am–2pm local;
 *   • already shown today (same LOCAL calendar day) → false, always;
 *   • outside the window (an evening first-open) → false — no fake morning;
 *   • an unparseable stamp is treated as never-shown (fails open to the
 *     window check, never to a permanent block).
 */
export function shouldShowStandup(
  lastShownAt: string | null | undefined,
  now: number | Date,
): boolean {
  const at = new Date(now);
  // Once per local day at ANY hour: the first session of the day gets the
  // standup even at 9pm — the operator's first-ever open was an evening and
  // the old 5am–2pm gate answered with silence. The greeting stays
  // time-honest (Morning/Afternoon/Evening) instead of a fake "Morning".
  if (lastShownAt) {
    const last = new Date(lastShownAt);
    if (!Number.isNaN(last.getTime()) && sameLocalDay(last, at)) return false;
  }
  return true;
}

/** Time-honest salutation for whatever hour the first session lands. */
export function standupGreetingWord(now: number | Date): string {
  const h = new Date(now).getHours();
  if (h >= 5 && h < 12) return 'Morning';
  if (h >= 12 && h < 17) return 'Afternoon';
  return 'Evening';
}

const clamp = (n: number) => (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/**
 * Format the standup from real counts. Returns null when there is genuinely
 * nothing ready (all counts zero) — never a fabricated line — and null
 * outside the morning window (defense in depth alongside shouldShowStandup).
 */
export function composeStandup(
  now: number | Date,
  inputs: StandupInputs,
): Standup | null {
  const drafts = clamp(inputs.drafts);
  const preppedPosts = clamp(inputs.preppedPosts);
  const pendingApprovals = clamp(inputs.pendingApprovals);
  // Reversible can never exceed the pending pool it's a subset of.
  const reversible = Math.min(clamp(inputs.reversibleApprovals ?? 0), pendingApprovals);
  const total = drafts + preppedPosts + pendingApprovals;
  if (total === 0) return null; // nothing real to report — honest silence

  const lines: string[] = [];
  if (drafts > 0) lines.push(plural(drafts, 'drafted email'));
  if (preppedPosts > 0) lines.push(plural(preppedPosts, 'prepped post'));
  if (pendingApprovals > 0) lines.push(plural(pendingApprovals, 'agent proposal'));

  const first = inputs.userName?.trim().split(/\s+/)[0];
  const word = standupGreetingWord(now);
  return {
    greeting: first ? `${word} ${first}.` : `${word}.`,
    lines,
    counts: { drafts, preppedPosts, pendingApprovals, reversible, total },
  };
}

/**
 * The cold-start standup — an empty workspace must NOT mean silence. When
 * there is genuinely nothing to brief (composeStandup → null), the system
 * still speaks first: it says so honestly and asks for the business. The
 * one thing worse than no smart greeting is a smart system that stays mute
 * exactly when the user most needs to be led.
 */
export function composeColdStartStandup(
  now: number | Date,
  userName?: string,
): Standup {
  const first = userName?.trim().split(/\s+/)[0];
  const word = standupGreetingWord(now);
  return {
    greeting: first ? `${word} ${first}.` : `${word}.`,
    lines: [],
    counts: { drafts: 0, preppedPosts: 0, pendingApprovals: 0, reversible: 0, total: 0 },
    coldStart: true,
  };
}
